import { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  usePutSources,
  usePostSourcesTestConnection,
  usePostSourcesBcTestConnection,
  usePostSourcesSalesforceTestConnection,
  getGetProfilesByIdQueryKey,
} from '@/api/control/control-plane/control-plane';
import type { UpdateSourceRequest, BcCompanyDto, SourceDto } from '@/api/control/missionImpactAPI.schemas';
import { useRoles } from '@/contexts/RolesContext';
import { Button } from '@/components/ui/button';
import { LoadingButton } from '@/components/LoadingButton';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Pencil, AlertCircle, Loader2, RefreshCw } from 'lucide-react';
import { ConnectionTestAlert } from '@/components/common/ConnectionTestAlert';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { useError } from '@/contexts/ErrorContext';
import { getEditSourceCredentialLabels } from '@/utils/sourceCredentialLabels';
import { AdvancedSettingsSection } from '@/components/profiles/settings/AdvancedSettingsSection';
import {
  SourcePageSizeField,
  pageSizeBounds,
  isPageSizeValid,
} from '@/components/profiles/settings/SourcePageSizeField';
import {
  DataverseColumnLengthsFields,
  hasOutOfRangeDataverseLength,
  DEFAULT_DATAVERSE_LENGTHS,
  type DataverseColumnLengthsValue,
} from '@/components/profiles/settings/DataverseColumnLengthsFields';

/**
 * Props for the EditSourceDialog component
 */
interface EditSourceDialogProps {
  /** The organization ID */
  orgId: string;
  /** The profile ID */
  profileId: string;
  /** The source to edit */
  source: SourceDto;
  /** Optional callback to execute after successful update */
  onSuccess?: () => void;
  /** Optional external control of dialog open state */
  externalOpen?: boolean;
  /** Optional callback when external open state should change */
  onExternalOpenChange?: (open: boolean) => void;
}

/**
 * Dialog for editing source configuration.
 * Handles the Dataverse, Business Central and Salesforce source types.
 */
export function EditSourceDialog({
  orgId,
  profileId,
  source,
  onSuccess,
  externalOpen,
  onExternalOpenChange,
}: EditSourceDialogProps) {
  const { isAdminOrOperator } = useRoles();
  const { t } = useLanguage();
  const { handleApiError } = useError();
  const queryClient = useQueryClient();
  const [internalOpen, setInternalOpen] = useState(false);

  const isBc = source.$type === 'BusinessCentral';
  const isSalesforce = source.$type === 'Salesforce';

  // Credential field labels differ per source type (Salesforce uses Consumer Key / Secret)
  const credentialLabels = getEditSourceCredentialLabels(source.$type, t);

  // Use external open state if provided, otherwise use internal state
  const isOpen = externalOpen ?? internalOpen;
  const setIsOpen = onExternalOpenChange ?? setInternalOpen;

  // Dataverse fields
  const [baseUrl, setBaseUrl] = useState('');

  // Per-source extraction page size (advanced). Seeded from the source on load; validated against
  // the source connector's range on save.
  const [pageSize, setPageSize] = useState(pageSizeBounds(source.$type).default);

  // Dataverse synthetic-column lengths (advanced, Dataverse only). Seeded from the source on load.
  const [columnLengths, setColumnLengths] = useState<DataverseColumnLengthsValue>(
    DEFAULT_DATAVERSE_LENGTHS,
  );

  // Business Central fields
  const [tenantId, setTenantId] = useState('');
  const [bcEnvironment, setBcEnvironment] = useState('');
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [selectedCompanyName, setSelectedCompanyName] = useState('');
  const [availableCompanies, setAvailableCompanies] = useState<BcCompanyDto[]>([]);

  // Salesforce fields
  const [sfInstanceUrl, setSfInstanceUrl] = useState('');
  const [sfIsSandbox, setSfIsSandbox] = useState(false);

  // Shared fields
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [connectionTestResult, setConnectionTestResult] = useState<null | 'success' | 'error'>(
    null,
  );
  const [connectionTestError, setConnectionTestError] = useState<string | null>(null);

  // Update form when source changes
  useEffect(() => {
    if (source.$type === 'BusinessCentral') {
      setTenantId(source.tenantId);
      setBcEnvironment(source.environment);
      setSelectedCompanyId(source.companyId);
      setSelectedCompanyName(source.companyName);
      setAvailableCompanies([]);
    } else if (source.$type === 'Dataverse') {
      setBaseUrl(source.baseUrl);
      setColumnLengths({
        dataverseLookupLabelMaxLength: source.dataverseLookupLabelMaxLength,
        dataverseChoiceLabelMaxLength: source.dataverseChoiceLabelMaxLength,
        dataverseMultiSelectMaxLength: source.dataverseMultiSelectMaxLength,
      });
    } else {
      setSfInstanceUrl(source.instanceUrl);
      setSfIsSandbox(source.isSandbox ?? false);
    }
    setPageSize(source.pageSize);
    setClientId(source.clientId);
    setClientSecret('');
  }, [source]);

  /** Resets connection test state */
  const resetConnectionState = () => {
    setConnectionTestResult(null);
    setConnectionTestError(null);
    setAvailableCompanies([]);
    setSelectedCompanyId('');
    setSelectedCompanyName('');
  };

  // Dataverse test connection mutation
  const { mutate: testDataverseConnection, isPending: isTestingDataverse } =
    usePostSourcesTestConnection({
      mutation: {
        onSuccess: () => {
          setConnectionTestResult('success');
          setConnectionTestError(null);
        },
        onError: (error) => {
          setConnectionTestResult('error');
          const { message } = handleApiError(
            error,
            { context: 'EditSourceDialog.testDataverseConnection' },
            { showToast: false },
          );
          setConnectionTestError(message);
        },
      },
    });

  // Business Central test connection mutation
  const { mutate: testBcConnection, isPending: isTestingBc } = usePostSourcesBcTestConnection({
    mutation: {
      onSuccess: (response) => {
        const companies = response.data.companies;
        setAvailableCompanies(companies);
        setConnectionTestResult('success');
        setConnectionTestError(null);
      },
      onError: (error) => {
        setConnectionTestResult('error');
        setAvailableCompanies([]);
        const { message } = handleApiError(
          error,
          { context: 'EditSourceDialog.testBcConnection' },
          { showToast: false },
        );
        setConnectionTestError(message);
      },
    },
  });

  // Salesforce test connection mutation
  const { mutate: testSalesforceConnection, isPending: isTestingSalesforce } =
    usePostSourcesSalesforceTestConnection({
      mutation: {
        onSuccess: () => {
          setConnectionTestResult('success');
          setConnectionTestError(null);
        },
        onError: (error) => {
          setConnectionTestResult('error');
          const { message } = handleApiError(
            error,
            { context: 'EditSourceDialog.testSalesforceConnection' },
            { showToast: false },
          );
          setConnectionTestError(message);
        },
      },
    });

  const isTesting = isTestingDataverse || isTestingBc || isTestingSalesforce;

  /**
   * Handles testing the connection with the current form values
   */
  const handleTestConnection = () => {
    setValidationError(null);
    resetConnectionState();

    if (isSalesforce) {
      if (!sfInstanceUrl.trim() || !clientId.trim() || !clientSecret.trim()) {
        return;
      }
      testSalesforceConnection({
        orgId,
        profileId,
        data: {
          instanceUrl: sfInstanceUrl.trim(),
          clientId: clientId.trim(),
          clientSecret: clientSecret.trim(),
          isSandbox: sfIsSandbox,
        },
      });
    } else if (isBc) {
      if (!tenantId.trim() || !bcEnvironment.trim() || !clientId.trim() || !clientSecret.trim()) {
        return;
      }
      testBcConnection({
        orgId,
        profileId,
        data: {
          tenantId: tenantId.trim(),
          environment: bcEnvironment.trim(),
          clientId: clientId.trim(),
          clientSecret: clientSecret.trim(),
        },
      });
    } else {
      if (!baseUrl.trim() || !clientId.trim() || !clientSecret.trim()) {
        return;
      }
      testDataverseConnection({
        orgId,
        profileId,
        data: {
          baseUrl: baseUrl.trim(),
          clientId: clientId.trim(),
          clientSecret: clientSecret.trim(),
        },
      });
    }
  };

  /** Handles company selection from the dropdown */
  const handleCompanySelect = (companyId: string) => {
    const company = availableCompanies.find((c) => c.id === companyId);
    setSelectedCompanyId(companyId);
    setSelectedCompanyName(company?.name ?? '');
  };

  const { mutate: updateSource, isPending: isUpdating } = usePutSources({
    mutation: {
      onSuccess: () => {
        toast.success(t.sources.editDialog.updateSuccess);
        setIsOpen(false);
        setValidationError(null);
        // Invalidate queries to refetch profile with updated source
        void queryClient.invalidateQueries({
          queryKey: ['sources', 'by-profile', profileId],
        });
        void queryClient.invalidateQueries({
          queryKey: getGetProfilesByIdQueryKey(orgId, profileId),
        });
        // Call optional onSuccess callback
        onSuccess?.();
      },
      onError: (error: unknown) => {
        const { message } = handleApiError(error, { context: 'EditSourceDialog.updateSource' });
        setValidationError(message);
      },
    },
  });

  /**
   * Handles the update action
   */
  const handleUpdate = () => {
    setValidationError(null);

    if (!isPageSizeValid(source.$type, pageSize)) {
      setValidationError(t.sources.editDialog.pageSizeRange);
      return;
    }

    if (isSalesforce) {
      if (!sfInstanceUrl.trim()) {
        setValidationError(t.sources.editDialog.instanceUrlRequired);
        return;
      }
      if (!clientId.trim()) {
        setValidationError(credentialLabels.clientIdRequired);
        return;
      }
      if (!clientSecret.trim()) {
        setValidationError(credentialLabels.clientSecretRequired);
        return;
      }

      const requestData: UpdateSourceRequest = {
        config: {
          $type: 'Salesforce',
          instanceUrl: sfInstanceUrl.trim(),
          clientId: clientId.trim(),
          clientSecret: clientSecret.trim(),
          isSandbox: sfIsSandbox,
          pageSize,
        },
      };
      updateSource({ orgId, profileId, sourceId: source.id, data: requestData });
    } else if (isBc) {
      if (!tenantId.trim()) {
        setValidationError(t.sources.editDialog.tenantIdRequired);
        return;
      }
      if (!bcEnvironment.trim()) {
        setValidationError(t.sources.editDialog.environmentRequired);
        return;
      }
      if (!clientId.trim()) {
        setValidationError(credentialLabels.clientIdRequired);
        return;
      }
      if (!clientSecret.trim()) {
        setValidationError(credentialLabels.clientSecretRequired);
        return;
      }
      if (!selectedCompanyId) {
        setValidationError(t.sources.editDialog.companyRequired);
        return;
      }

      const requestData: UpdateSourceRequest = {
        config: {
          $type: 'BusinessCentral',
          tenantId: tenantId.trim(),
          environment: bcEnvironment.trim(),
          clientId: clientId.trim(),
          clientSecret: clientSecret.trim(),
          companyId: selectedCompanyId,
          companyName: selectedCompanyName,
          pageSize,
        },
      };
      updateSource({ orgId, profileId, sourceId: source.id, data: requestData });
    } else {
      if (!baseUrl.trim()) {
        setValidationError(t.sources.editDialog.baseUrlRequired);
        return;
      }
      if (!clientId.trim()) {
        setValidationError(credentialLabels.clientIdRequired);
        return;
      }
      if (!clientSecret.trim()) {
        setValidationError(credentialLabels.clientSecretRequired);
        return;
      }
      const requestData: UpdateSourceRequest = {
        config: {
          $type: 'Dataverse',
          baseUrl: baseUrl.trim(),
          clientId: clientId.trim(),
          clientSecret: clientSecret.trim(),
          pageSize,
          dataverseLookupLabelMaxLength:
            columnLengths.dataverseLookupLabelMaxLength ??
            DEFAULT_DATAVERSE_LENGTHS.dataverseLookupLabelMaxLength,
          dataverseChoiceLabelMaxLength:
            columnLengths.dataverseChoiceLabelMaxLength ??
            DEFAULT_DATAVERSE_LENGTHS.dataverseChoiceLabelMaxLength,
          dataverseMultiSelectMaxLength:
            columnLengths.dataverseMultiSelectMaxLength ??
            DEFAULT_DATAVERSE_LENGTHS.dataverseMultiSelectMaxLength,
        },
      };
      updateSource({ orgId, profileId, sourceId: source.id, data: requestData });
    }
  };

  /**
   * Handles dialog close and resets form
   */
  const handleClose = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      // Reset form to current source values
      if (source.$type === 'BusinessCentral') {
        setTenantId(source.tenantId);
        setBcEnvironment(source.environment);
        setSelectedCompanyId(source.companyId);
        setSelectedCompanyName(source.companyName);
        setAvailableCompanies([]);
      } else if (source.$type === 'Dataverse') {
        setBaseUrl(source.baseUrl);
        setColumnLengths({
          dataverseLookupLabelMaxLength: source.dataverseLookupLabelMaxLength,
          dataverseChoiceLabelMaxLength: source.dataverseChoiceLabelMaxLength,
          dataverseMultiSelectMaxLength: source.dataverseMultiSelectMaxLength,
        });
      } else {
        setSfInstanceUrl(source.instanceUrl);
        setSfIsSandbox(source.isSandbox ?? false);
      }
      setClientId(source.clientId);
      setClientSecret('');
      setValidationError(null);
      setConnectionTestResult(null);
      setConnectionTestError(null);
    }
  };

  /** Whether all connection fields required by the current source type have a value */
  const areRequiredFieldsFilled = isSalesforce
    ? !!sfInstanceUrl.trim() && !!clientId.trim() && !!clientSecret.trim()
    : isBc
      ? !!tenantId.trim() && !!bcEnvironment.trim() && !!clientId.trim() && !!clientSecret.trim()
      : !!baseUrl.trim() && !!clientId.trim() && !!clientSecret.trim();

  // Original Dataverse lengths (for the soft reduce-warning); undefined for non-Dataverse sources.
  const dataverseOriginal =
    source.$type === 'Dataverse'
      ? {
          dataverseLookupLabelMaxLength: source.dataverseLookupLabelMaxLength,
          dataverseChoiceLabelMaxLength: source.dataverseChoiceLabelMaxLength,
          dataverseMultiSelectMaxLength: source.dataverseMultiSelectMaxLength,
        }
      : undefined;

  /** Whether any configured Dataverse column length is outside the allowed range. */
  const hasOutOfRangeLength = hasOutOfRangeDataverseLength(columnLengths);

  /** Whether the Update button should be enabled */
  const isUpdateEnabled =
    areRequiredFieldsFilled &&
    connectionTestResult === 'success' &&
    (!isBc || !!selectedCompanyId) &&
    !hasOutOfRangeLength;

  /** Whether the test connection button should be enabled */
  const isTestEnabled = areRequiredFieldsFilled;

  // Hide built-in trigger when in controlled mode (externalOpen is provided)
  const isControlled = externalOpen !== undefined;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      {isAdminOrOperator && !isControlled && (
        <DialogTrigger asChild>
          <Button variant="outline">
            <Pencil className="mr-2 h-4 w-4" />
            {t.sources.editDialog.buttonText}
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>{t.sources.editDialog.title}</DialogTitle>
          <DialogDescription>{t.sources.editDialog.description}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          {validationError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{validationError}</AlertDescription>
            </Alert>
          )}

          {/* Dataverse Fields */}
          {!isBc && !isSalesforce && (
            <>
              <div className="grid gap-2">
                <Label htmlFor="editBaseUrl">{t.sources.editDialog.baseUrl} *</Label>
                <Input
                  id="editBaseUrl"
                  value={baseUrl}
                  onChange={(e) => {
                    setBaseUrl(e.target.value);
                    resetConnectionState();
                  }}
                  placeholder="https://org.crm.dynamics.com/api/data/v9.2"
                  className="font-mono text-sm"
                  disabled={isTesting}
                />
                <p className="text-xs text-muted-foreground">{t.sources.editDialog.baseUrlDescription}</p>
              </div>
            </>
          )}

          {/* Business Central Fields */}
          {isBc && (
            <>
              <div className="grid gap-2">
                <Label htmlFor="editTenantId">{t.wizard.source.tenantId} *</Label>
                <Input
                  id="editTenantId"
                  value={tenantId}
                  onChange={(e) => {
                    setTenantId(e.target.value);
                    resetConnectionState();
                  }}
                  placeholder={t.wizard.source.tenantIdPlaceholder}
                  className="font-mono text-sm"
                  disabled={isTesting}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="editBcEnvironment">{t.wizard.source.bcEnvironment} *</Label>
                <Input
                  id="editBcEnvironment"
                  value={bcEnvironment}
                  onChange={(e) => {
                    setBcEnvironment(e.target.value);
                    resetConnectionState();
                  }}
                  placeholder={t.wizard.source.bcEnvironmentPlaceholder}
                  className="font-mono text-sm"
                  disabled={isTesting}
                />
              </div>
            </>
          )}

          {/* Salesforce Fields */}
          {isSalesforce && (
            <>
              <div className="grid gap-2">
                <Label htmlFor="editSfInstanceUrl">{t.wizard.source.instanceUrl} *</Label>
                <Input
                  id="editSfInstanceUrl"
                  value={sfInstanceUrl}
                  onChange={(e) => {
                    setSfInstanceUrl(e.target.value);
                    resetConnectionState();
                  }}
                  placeholder={t.wizard.source.instanceUrlPlaceholder}
                  className="font-mono text-sm"
                  disabled={isTesting}
                />
              </div>
              <div className="flex items-center gap-3">
                <Checkbox
                  id="editSfIsSandbox"
                  checked={sfIsSandbox}
                  onCheckedChange={(checked) => {
                    setSfIsSandbox(checked === true);
                    resetConnectionState();
                  }}
                  disabled={isTesting}
                />
                <div>
                  <Label htmlFor="editSfIsSandbox" className="cursor-pointer">
                    {t.wizard.source.isSandbox}
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    {t.wizard.source.isSandboxDescription}
                  </p>
                </div>
              </div>
            </>
          )}

          {/* Shared Fields */}
          <div className="grid gap-2">
            <Label htmlFor="editClientId">{credentialLabels.clientId} *</Label>
            <Input
              id="editClientId"
              type="text"
              value={clientId}
              onChange={(e) => {
                setClientId(e.target.value);
                resetConnectionState();
              }}
              placeholder={credentialLabels.clientIdPlaceholder}
              className="font-mono text-sm"
              disabled={isTesting}
            />
            <p className="text-xs text-muted-foreground">{credentialLabels.clientIdDescription}</p>
            {source.$type === 'Dataverse' && (
              <p className="text-xs text-muted-foreground">{t.sources.sourceCapacity.dedicatedAppNote}</p>
            )}
          </div>
          <div className="grid gap-2">
            <Label htmlFor="editClientSecret">{credentialLabels.clientSecret} *</Label>
            <Input
              id="editClientSecret"
              type="password"
              value={clientSecret}
              onChange={(e) => {
                setClientSecret(e.target.value);
                resetConnectionState();
              }}
              placeholder={credentialLabels.clientSecretPlaceholder}
              className="font-mono text-sm"
              disabled={isTesting}
            />
            <p className="text-xs text-muted-foreground">
              {credentialLabels.clientSecretDescription}
            </p>
          </div>

          {/* Advanced (replication-impacting) per-source settings — opt-in. */}
          <AdvancedSettingsSection
            title={t.wizard.advancedSettings.title}
            description={t.wizard.advancedSettings.description}
          >
            <SourcePageSizeField
              sourceType={source.$type}
              value={pageSize}
              onChange={setPageSize}
              disabled={isTesting}
            />
            {!isBc && !isSalesforce && (
              <DataverseColumnLengthsFields
                value={columnLengths}
                onChange={setColumnLengths}
                disabled={isTesting}
                original={dataverseOriginal}
              />
            )}
          </AdvancedSettingsSection>

          {/* Test Connection Button — hidden for BC once companies are loaded */}
          {!(isBc && connectionTestResult === 'success') && (
            <LoadingButton
              type="button"
              variant="outline"
              onClick={handleTestConnection}
              isLoading={isTesting}
              disabled={!isTestEnabled}
              className="w-full"
            >
              {isBc
                ? t.wizard.source.testConnectionAndLoadCompanies
                : t.sources.editDialog.testConnection}
            </LoadingButton>
          )}

          {/* Connection Test Result */}
          {connectionTestResult === 'success' && (
            <ConnectionTestAlert success={true} message={t.sources.editDialog.connectionSuccessful} />
          )}
          {connectionTestResult === 'error' && connectionTestError && (
            <ConnectionTestAlert success={false} message={connectionTestError} />
          )}

          {/* Business Central Company Selector */}
          {isBc && connectionTestResult === 'success' && (
            <div className="grid gap-2">
              <Label htmlFor="editCompany">{t.wizard.source.company} *</Label>
              <div className="flex gap-2">
                <Select value={selectedCompanyId} onValueChange={handleCompanySelect}>
                  <SelectTrigger id="editCompany" className="flex-1">
                    <SelectValue placeholder={t.wizard.source.companyPlaceholder} />
                  </SelectTrigger>
                  <SelectContent>
                    {availableCompanies.length === 0 ? (
                      <SelectItem value="__none__" disabled>
                        {t.wizard.source.noCompaniesFound}
                      </SelectItem>
                    ) : (
                      availableCompanies.map((company) => (
                        <SelectItem key={company.id} value={company.id}>
                          {company.name}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={handleTestConnection}
                  disabled={isTestingBc}
                  title={t.wizard.source.testConnectionAndLoadCompanies}
                >
                  {isTestingBc
                    ? <Loader2 className="h-4 w-4 animate-spin" />
                    : <RefreshCw className="h-4 w-4" />
                  }
                </Button>
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              handleClose(false);
            }}
          >
            {t.sources.editDialog.cancel}
          </Button>
          <LoadingButton onClick={handleUpdate} disabled={!isUpdateEnabled} isLoading={isUpdating}>
            {t.sources.editDialog.update}
          </LoadingButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
